<script setup lang="ts">
import {
  Check,
  LoaderCircle,
  Pencil,
  Plus,
  RefreshCw,
  X,
} from "lucide-vue-next"
import { computed, onMounted, ref } from "vue"

import AppActionDialog from "../../components/AppActionDialog.vue"
import AppSelect from "../../components/AppSelect.vue"
import { ApiError, apiRequest } from "../../lib/api"
import { formatShanghaiTime } from "../../lib/datetime"
import type {
  AdminBillingOverview,
  AdminPurchaseOrderItem,
  BillingPlanItem,
  TopupPackageItem,
} from "../../lib/types"
import { useSessionStore } from "../../stores/session"

type CatalogKind = "plan" | "topup"
type AdminTab = "orders" | "catalog" | "subscriptions"

const session = useSessionStore()
const overview = ref<AdminBillingOverview | null>(null)
const loading = ref(true)
const error = ref("")
const activeTab = ref<AdminTab>("orders")
const orderFilter = ref("pending")
const actionId = ref("")
const reviewTarget = ref<{
  order: AdminPurchaseOrderItem
  action: "approve" | "reject"
} | null>(null)
const reviewNote = ref("")
const editorOpen = ref(false)
const editorKind = ref<CatalogKind>("plan")
const editingId = ref("")
const saving = ref(false)
const orderFilterOptions = [
  { value: "pending", label: "待确认" },
  { value: "", label: "全部状态" },
  { value: "fulfilled", label: "已生效" },
  { value: "rejected", label: "已拒绝" },
  { value: "canceled", label: "已取消" },
]
const catalogStatusOptions = [
  { value: "active", label: "启用" },
  { value: "archived", label: "下架" },
]
const form = ref({
  code: "",
  name: "",
  description: "",
  price_minor: 1900,
  currency: "USD",
  credits: 2000,
  period_days: 30,
  validity_days: 365,
  features_text: "",
  sort_order: 0,
  status: "active" as "active" | "archived",
})

const filteredOrders = computed(() => {
  const orders = overview.value?.orders ?? []
  return orderFilter.value ? orders.filter((order) => order.status === orderFilter.value) : orders
})
const pendingCount = computed(
  () => overview.value?.orders.filter((order) => order.status === "pending").length ?? 0,
)

onMounted(load)

async function load(): Promise<void> {
  loading.value = true
  error.value = ""
  try {
    overview.value = await apiRequest<AdminBillingOverview>("/v1/admin/billing")
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "加载套餐与订单失败"
  } finally {
    loading.value = false
  }
}

function openCreate(kind: CatalogKind): void {
  editorKind.value = kind
  editingId.value = ""
  form.value = {
    code: "",
    name: "",
    description: "",
    price_minor: kind === "plan" ? 1900 : 1200,
    currency: "USD",
    credits: kind === "plan" ? 2000 : 1000,
    period_days: 30,
    validity_days: 365,
    features_text: "",
    sort_order: 0,
    status: "active",
  }
  editorOpen.value = true
}

function editPlan(plan: BillingPlanItem): void {
  editorKind.value = "plan"
  editingId.value = plan.id
  form.value = {
    code: plan.code,
    name: plan.name,
    description: plan.description ?? "",
    price_minor: plan.monthly_price_minor,
    currency: plan.currency,
    credits: plan.monthly_credits,
    period_days: plan.period_days,
    validity_days: 365,
    features_text: plan.features.join("\n"),
    sort_order: plan.sort_order,
    status: plan.status,
  }
  editorOpen.value = true
}

function editTopup(item: TopupPackageItem): void {
  editorKind.value = "topup"
  editingId.value = item.id
  form.value = {
    code: item.code,
    name: item.name,
    description: item.description ?? "",
    price_minor: item.price_minor,
    currency: item.currency,
    credits: item.credits,
    period_days: 30,
    validity_days: item.validity_days ?? 365,
    features_text: "",
    sort_order: item.sort_order,
    status: item.status,
  }
  editorOpen.value = true
}

async function saveCatalog(): Promise<void> {
  saving.value = true
  error.value = ""
  const kind = editorKind.value
  const path = kind === "plan" ? "plans" : "topups"
  const isEditing = Boolean(editingId.value)
  const body =
    kind === "plan"
      ? {
          ...(!isEditing ? { code: form.value.code } : {}),
          name: form.value.name,
          description: form.value.description || null,
          monthly_price_minor: form.value.price_minor,
          currency: form.value.currency.toUpperCase(),
          monthly_credits: form.value.credits,
          period_days: form.value.period_days,
          features: form.value.features_text.split("\n").map((item) => item.trim()).filter(Boolean),
          sort_order: form.value.sort_order,
          status: form.value.status,
        }
      : {
          ...(!isEditing ? { code: form.value.code } : {}),
          name: form.value.name,
          description: form.value.description || null,
          price_minor: form.value.price_minor,
          currency: form.value.currency.toUpperCase(),
          credits: form.value.credits,
          validity_days: form.value.validity_days,
          sort_order: form.value.sort_order,
          status: form.value.status,
        }
  try {
    await apiRequest(
      `/v1/admin/billing/${path}${isEditing ? `/${editingId.value}` : ""}`,
      {
        method: isEditing ? "PATCH" : "POST",
        csrfToken: session.csrfToken,
        body,
      },
    )
    editorOpen.value = false
    await load()
  } catch (cause) {
    error.value = cause instanceof ApiError ? cause.message : "保存失败"
  } finally {
    saving.value = false
  }
}

async function review(): Promise<void> {
  if (!reviewTarget.value) return
  const { order, action } = reviewTarget.value
  actionId.value = order.id
  error.value = ""
  try {
    await apiRequest(`/v1/admin/billing/orders/${order.id}/review`, {
      method: "POST",
      csrfToken: session.csrfToken,
      body: { action, note: reviewNote.value.trim() || null },
    })
    reviewTarget.value = null
    await load()
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "审核失败"
  } finally {
    actionId.value = ""
  }
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat("zh-CN", { style: "currency", currency }).format(value / 100)
}

function formatDate(value: string): string {
  return formatShanghaiTime(value, { dateStyle: "short", timeStyle: "short" })
}

function orderStatus(value: AdminPurchaseOrderItem["status"]): string {
  return {
    pending: "待确认",
    fulfilled: "已生效",
    rejected: "已拒绝",
    canceled: "已取消",
    refunded: "已退款",
  }[value]
}
</script>

<template>
  <section class="admin-page">
    <div class="admin-page-heading">
      <div><span class="eyebrow">Plans & orders</span><h1>套餐与订单</h1><p>配置套餐和充值包，审核用户申请并发放额度。</p></div>
      <div class="admin-count"><strong>{{ pendingCount }}</strong><span>待处理订单</span></div>
    </div>

    <div class="billing-tabs">
      <button :class="{ active: activeTab === 'orders' }" @click="activeTab = 'orders'">订单审核 <span>{{ pendingCount }}</span></button>
      <button :class="{ active: activeTab === 'catalog' }" @click="activeTab = 'catalog'">商品配置</button>
      <button :class="{ active: activeTab === 'subscriptions' }" @click="activeTab = 'subscriptions'">生效订阅</button>
      <button class="billing-refresh" @click="load"><RefreshCw :class="{ spin: loading }" :size="14" /></button>
    </div>

    <p v-if="error" class="form-error">{{ error }}</p>
    <div v-if="loading" class="admin-loading billing-main-loading"><LoaderCircle class="spin" :size="20" />正在加载</div>

    <template v-else-if="overview">
      <section v-if="activeTab === 'orders'">
        <div class="admin-toolbar">
          <AppSelect v-model="orderFilter" class="admin-select" compact aria-label="订单状态" :options="orderFilterOptions" />
        </div>
        <div class="admin-table-shell">
          <div v-if="filteredOrders.length === 0" class="admin-empty">当前筛选下没有订单。</div>
          <div v-else class="responsive-table">
            <table class="admin-table billing-order-table">
              <thead><tr><th>时间（上海）</th><th>用户 / Workspace</th><th>商品</th><th>金额 / 额度</th><th>状态</th><th>操作</th></tr></thead>
              <tbody>
                <tr v-for="order in filteredOrders" :key="order.id">
                  <td>{{ formatDate(order.created_at) }}</td>
                  <td><strong>{{ order.requested_by_email }}</strong><small>{{ order.workspace_name }}</small></td>
                  <td><strong>{{ order.product_name }}</strong><small>{{ order.kind === "subscription" ? "月度套餐" : "按量充值" }} · {{ order.payment_provider }}</small></td>
                  <td><strong>{{ formatMoney(order.amount_minor, order.currency) }}</strong><small>{{ order.credits.toLocaleString() }} Credits</small></td>
                  <td><span class="status-pill" :class="{ 'status-pill--active': order.status === 'fulfilled', 'status-pill--expired': order.status === 'rejected' }"><i></i>{{ orderStatus(order.status) }}</span><small v-if="order.admin_note">{{ order.admin_note }}</small></td>
                  <td><div v-if="order.status === 'pending'" class="order-actions"><button class="approve-button" :disabled="actionId === order.id" @click="reviewNote = '管理员手动确认'; reviewTarget = { order, action: 'approve' }"><Check :size="13" />批准</button><button class="reject-button" :disabled="actionId === order.id" @click="reviewNote = '申请未通过'; reviewTarget = { order, action: 'reject' }"><X :size="13" />拒绝</button></div><span v-else>—</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section v-else-if="activeTab === 'catalog'" class="catalog-section">
        <div class="catalog-heading"><div><span class="eyebrow">Subscription plans</span><h2>月度套餐</h2></div><button class="primary-button" @click="openCreate('plan')"><Plus :size="14" />新建套餐</button></div>
        <div class="catalog-grid">
          <article v-for="plan in overview.plans" :key="plan.id" class="catalog-card">
            <header><div><strong>{{ plan.name }}</strong><code>{{ plan.code }}</code></div><span class="status-pill" :class="{ 'status-pill--active': plan.status === 'active' }"><i></i>{{ plan.status }}</span></header>
            <div class="catalog-price">{{ formatMoney(plan.monthly_price_minor, plan.currency) }} <small>/ {{ plan.period_days }} 天</small></div>
            <p>{{ plan.monthly_credits.toLocaleString() }} Credits / 期</p>
            <footer><span>排序 {{ plan.sort_order }}</span><button class="secondary-button" @click="editPlan(plan)"><Pencil :size="13" />编辑</button></footer>
          </article>
        </div>

        <div class="catalog-heading catalog-heading--topup"><div><span class="eyebrow">Top-up packages</span><h2>按量充值包</h2></div><button class="primary-button" @click="openCreate('topup')"><Plus :size="14" />新建充值包</button></div>
        <div class="catalog-grid">
          <article v-for="item in overview.topup_packages" :key="item.id" class="catalog-card">
            <header><div><strong>{{ item.name }}</strong><code>{{ item.code }}</code></div><span class="status-pill" :class="{ 'status-pill--active': item.status === 'active' }"><i></i>{{ item.status }}</span></header>
            <div class="catalog-price">{{ formatMoney(item.price_minor, item.currency) }}</div>
            <p>{{ item.credits.toLocaleString() }} Credits · {{ item.validity_days ?? "永久" }} 天</p>
            <footer><span>排序 {{ item.sort_order }}</span><button class="secondary-button" @click="editTopup(item)"><Pencil :size="13" />编辑</button></footer>
          </article>
        </div>
      </section>

      <section v-else>
        <div class="admin-table-shell subscription-table">
          <div v-if="overview.subscriptions.length === 0" class="admin-empty">当前没有生效中的套餐。</div>
          <div v-else class="responsive-table">
            <table class="admin-table">
              <thead><tr><th>Workspace</th><th>套餐</th><th>本期开始</th><th>本期结束</th><th>状态</th></tr></thead>
              <tbody><tr v-for="item in overview.subscriptions" :key="item.id"><td><strong>{{ item.workspace_name }}</strong></td><td>{{ item.plan_name }}</td><td>{{ formatDate(item.current_period_start) }}</td><td>{{ formatDate(item.current_period_end) }}</td><td><span class="status-pill status-pill--active"><i></i>{{ item.status }}</span></td></tr></tbody>
            </table>
          </div>
        </div>
      </section>
    </template>

    <div v-if="editorOpen" class="modal-backdrop" @click.self="editorOpen = false">
      <form class="modal billing-editor" @submit.prevent="saveCatalog">
        <div class="modal__heading">
          <div>
            <span class="eyebrow">{{ editorKind === "plan" ? "Subscription plan" : "Top-up package" }}</span>
            <h2>{{ editingId ? "编辑" : "新建" }}{{ editorKind === "plan" ? "套餐" : "充值包" }}</h2>
            <p>设置展示价格、到账额度与可用周期。</p>
          </div>
          <button type="button" class="icon-button" aria-label="关闭" @click="editorOpen = false"><X :size="18" /></button>
        </div>
        <div class="editor-grid">
          <label class="field"><span>代码</span><input v-model="form.code" required :disabled="Boolean(editingId)" pattern="[a-z][a-z0-9_-]{1,31}" /></label>
          <label class="field"><span>显示名称</span><input v-model="form.name" required maxlength="120" /></label>
          <label class="field editor-wide"><span>说明</span><input v-model="form.description" maxlength="500" /></label>
          <label class="field"><span>价格（最小货币单位）</span><input v-model.number="form.price_minor" type="number" min="0" required /></label>
          <label class="field"><span>币种</span><input v-model="form.currency" required maxlength="3" /></label>
          <label class="field"><span>Credits</span><input v-model.number="form.credits" type="number" min="1" required /></label>
          <label v-if="editorKind === 'plan'" class="field"><span>周期天数</span><input v-model.number="form.period_days" type="number" min="1" required /></label>
          <label v-else class="field"><span>有效天数</span><input v-model.number="form.validity_days" type="number" min="1" required /></label>
          <label class="field"><span>排序</span><input v-model.number="form.sort_order" type="number" min="0" /></label>
          <label class="field"><span>状态</span><AppSelect v-model="form.status" aria-label="状态" :options="catalogStatusOptions" /></label>
          <label v-if="editorKind === 'plan'" class="field editor-wide"><span>权益（每行一条）</span><textarea v-model="form.features_text" rows="4"></textarea></label>
        </div>
        <div class="modal__actions"><button type="button" class="secondary-button" @click="editorOpen = false">取消</button><button class="primary-button" :disabled="saving"><LoaderCircle v-if="saving" class="spin" :size="15" />保存</button></div>
      </form>
    </div>

    <AppActionDialog
      v-model:input-value="reviewNote"
      :open="Boolean(reviewTarget)"
      eyebrow="Order review"
      :title="reviewTarget?.action === 'approve' ? '批准并发放额度' : '拒绝套餐申请'"
      :description="reviewTarget?.action === 'approve' ? '确认后将立即创建订阅或发放额度。' : '拒绝后不会向用户发放额度。'"
      :detail="reviewTarget?.order.product_name ?? ''"
      :confirm-label="reviewTarget?.action === 'approve' ? '批准申请' : '确认拒绝'"
      :danger="reviewTarget?.action === 'reject'"
      :busy="Boolean(actionId)"
      input-label="审核备注"
      input-hint="备注会展示在用户的申请记录中，可留空。"
      @close="reviewTarget = null"
      @confirm="review"
    />
  </section>
</template>

<style scoped>
.billing-tabs { display: flex; align-items: center; gap: 3px; margin: 22px 0; padding: 3px; border: 1px solid var(--line); background: var(--panel-muted); }
.billing-tabs button { min-height: 36px; display: inline-flex; align-items: center; gap: 7px; padding: 0 13px; color: var(--muted); background: transparent; cursor: pointer; font-size: 11px; }
.billing-tabs button.active { color: var(--ink); background: var(--panel); box-shadow: 0 1px 4px color-mix(in oklch, var(--ink) 10%, transparent); font-weight: 650; }
.billing-tabs button span { padding: 2px 6px; border-radius: 99px; color: var(--panel); background: var(--accent); font: 650 9px monospace; }
.billing-tabs .billing-refresh { margin-left: auto; }
.billing-main-loading { min-height: 320px; }
.billing-order-table { min-width: 1050px; }
.billing-order-table td { height: 78px; }
.billing-order-table td small { display: block; max-width: 220px; margin-top: 4px; overflow: hidden; color: var(--muted); font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.order-actions { display: flex; gap: 6px; }
.order-actions button { min-height: 31px; display: inline-flex; align-items: center; gap: 5px; padding: 0 9px; cursor: pointer; font-size: 10px; font-weight: 650; }
.approve-button { color: var(--success); border: 1px solid color-mix(in oklch, var(--success) 30%, var(--line)); background: var(--success-soft); }
.reject-button { color: var(--accent); border: 1px solid color-mix(in oklch, var(--accent) 25%, var(--line)); background: var(--accent-soft); }
.catalog-heading { display: flex; align-items: end; justify-content: space-between; gap: 20px; margin-bottom: 14px; padding-bottom: 12px; border-bottom: 1px solid var(--line); }
.catalog-heading h2 { margin: 5px 0 0; font-size: 19px; }
.catalog-heading--topup { margin-top: 42px; }
.catalog-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.catalog-card { min-width: 0; padding: 17px; border: 1px solid var(--line); background: var(--panel); }
.catalog-card header { display: flex; align-items: start; justify-content: space-between; gap: 12px; }
.catalog-card header > div { min-width: 0; display: grid; gap: 4px; }
.catalog-card header strong { font-size: 14px; }
.catalog-card header code { overflow: hidden; color: var(--muted); font-size: 9px; text-overflow: ellipsis; }
.catalog-price { margin-top: 20px; font: 700 22px monospace; }
.catalog-price small { color: var(--muted); font-size: 9px; }
.catalog-card p { color: var(--muted); font-size: 10px; }
.catalog-card footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 17px; padding-top: 13px; border-top: 1px solid var(--line); color: var(--muted); font-size: 9px; }
.subscription-table { margin-top: 0; }
.billing-editor { width: min(760px, 100%); }
.editor-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px 16px; }
.editor-wide { grid-column: 1 / -1; }
.field textarea { width: 100%; padding: 12px 13px; border: 1px solid var(--line-strong); border-radius: 9px; color: var(--ink); background: var(--panel); resize: vertical; outline: 0; font: inherit; line-height: 1.65; transition: border-color 140ms ease, box-shadow 140ms ease; }
.field textarea:hover { border-color: color-mix(in oklch, var(--ink) 52%, var(--line-strong)); }
.field textarea:focus { border-color: var(--accent); box-shadow: 0 0 0 3px color-mix(in oklch, var(--accent) 13%, transparent); }

@media (max-width: 900px) {
  .catalog-grid { grid-template-columns: 1fr; }
}
@media (max-width: 650px) {
  .billing-tabs { align-items: stretch; flex-direction: column; }
  .billing-tabs .billing-refresh { margin-left: 0; }
  .catalog-heading { align-items: start; flex-direction: column; }
  .editor-grid { grid-template-columns: 1fr; }
  .editor-wide { grid-column: auto; }
}
</style>
